import { H4 } from 'components/headings';
import Layout from 'components/layout';
import { StyledBlockContent } from 'components/rich-text/rich-text.styles';
import SEO from 'components/seo';
import ContentSection from 'components/ui/content-section';
import { GetStaticProps, NextPage } from 'next';
import Link from 'next/link';
import { useQuery } from 'react-query';
import slugify from 'slugify';
import { backgroundImages } from 'utils/config';
import { getUrlsFromXMLString, titleize } from 'utils/helpers';
import Custom404 from './404';

const PAGE_TITLE = 'Sitemap';

interface Props {
  sitemap?: string[];
  errorCode?: number;
}

const Sitemap: NextPage<Props> = (props) => {
  const { data, isFetching, error } = useQuery(['sitemap'], getSitemap, {
    initialData: props.sitemap,
    enabled: !props.sitemap,
  });

  if (props.errorCode && !data && !isFetching) {
    return <Custom404 />;
  }

  return (
    <>
      <SEO title={PAGE_TITLE} />
      <Layout mainBackground={backgroundImages.treesBurn}>
        <ContentSection>
          <H4 as='h1'>{PAGE_TITLE}</H4>
          <StyledBlockContent as='div'>
            {isFetching && <p>Loading...</p>}

            {!isFetching && error && !props.sitemap && (
              <p>An error has occurred.</p>
            )}

            {data && (
              <ul>
                {data.map((url) => {
                  const parts = url.split('/');
                  const key = slugify(url);
                  if (url === '') {
                    return (
                      <li key='home'>
                        <Link href='/'>Home</Link>
                      </li>
                    );
                  } else {
                    return (
                      <li key={key}>
                        <Link href={url}>
                          <a>
                            {parts.reduce(
                              (path, part, i) =>
                                i !== parts.length - 1
                                  ? (path += `${titleize(part)} > `)
                                  : (path += titleize(part)),
                              ''
                            )}
                          </a>
                        </Link>
                      </li>
                    );
                  }
                })}
              </ul>
            )}
          </StyledBlockContent>
        </ContentSection>
      </Layout>
    </>
  );
};

async function getSitemap() {
  return fetch(`${process.env.NEXT_PUBLIC_APP_URL}/sitemap.xml`).then((res) => {
    if (res.ok) {
      return res.text().then(getUrlsFromXMLString);
    } else {
      return Promise.resolve(undefined);
    }
  });
}

export const getStaticProps: GetStaticProps = async () => {
  const errorProps = {
    errorCode: 404,
    sitemap: null,
  };

  try {
    const sitemap = await getSitemap();

    if (!sitemap?.length) {
      return {
        props: errorProps,
      };
    }

    return {
      props: { sitemap },
    };
  } catch (error) {
    return {
      props: errorProps,
    };
  }
};

export default Sitemap;
